import { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi } from '../../lib/api';
import type { ActiveFormImportJob, FormImportJobResult, FormImportProgress } from '../../lib/api';
import {
  describeFormImportOutcome,
  formImportOutcomeToastType,
  formImportProducedDraft,
} from './formImportOutcome';
import { runJobRecovery, useJobPolling } from '../../hooks/useJobPolling';
import { useElapsedSeconds } from '../../hooks/useElapsedSeconds';
import type { ToastType } from '../../stores/toast';

/**
 * 양식 업로드(form-import) Job의 **소유자** — 폴링·복구·수명주기를 페이지 레벨로 끌어올린다.
 *
 * 종전에는 모달이 useJobPolling을 직접 들고 있어서, 모달을 닫으면 폴링이 끊겼다. 그래서
 * "분석 중에는 닫기 자체를 막는" 처방이 필요했고(최대 5분 동안 에디터가 잠긴다), 진행 상황을
 * 모달 밖에서 보여 줄 방법도 없었다. Job을 페이지가 소유하면 모달은 **표시자**가 되고
 * (업로드 시작만 트리거) 헤더 pill이 같은 상태를 읽는다 — 폴링은 한 곳에서만 돈다.
 *
 * 새로고침 복구(runJobRecovery)도 여기로 옮긴다. 복구는 "페이지에 들어왔을 때 살아 있는 Job을
 * 이어받는 것"이라 Job 소유자와 같은 자리에 있는 게 맞고, 종전처럼 모달에 resumeJobId를
 * 넘길 필요가 없어진다(모달은 열려 있든 닫혀 있든 같은 state를 본다).
 *
 * **모달은 사용자가 열 때만 열린다** — 복구든 완료든 자동으로 열지 않는다. 분리 상태에서는
 * 사용자가 다른 템플릿을 편집하는 중일 수 있어, 창이 튀어나오면 그 작업을 가로챈다(미저장
 * 변경이 있으면 더 나쁘다). 알림은 토스트, 상시 표시는 헤더 pill이 맡는다.
 *
 * 설계 정본: docs/decisions/2026-07-27-composer-form-import-ir.md
 */

/**
 * Job 수명주기 단계 — 모달의 화면 단계(select/importing/result/error)와 1:1이되, 'idle'은
 * 모달에서 'select'(파일 고르는 중)로 읽힌다. 파일 선택 상태는 Job이 아니라 모달의 것이다.
 */
export type FormImportPhase = 'idle' | 'importing' | 'result' | 'error';

export interface FormImportJobState {
  phase: FormImportPhase;
  progress: FormImportProgress | null;
  result: FormImportJobResult | null;
  error: string | null;
  /** 실패 시 마지막 AI 원문 — 프롬프트 튜닝 루프용 진단 자료(모달에서 접어서 노출). */
  rawResponse: string | null;
  /**
   * 분석 시작 후 경과 초 — 모달과 헤더 pill이 **같은 값**을 봐야 하므로 여기서 한 번만 센다
   * (각자 useElapsedSeconds를 부르면 기준 시각이 갈려 두 표시가 어긋난다).
   */
  elapsedSeconds: number;
}

export interface UseFormImportJobOptions {
  /** 피처 플래그 등으로 페이지가 비활성이면 복구를 시도하지 않는다. */
  enabled: boolean;
  showToast: (message: string, type?: ToastType) => void;
}

export interface UseFormImportJob {
  state: FormImportJobState;
  modalOpen: boolean;
  /** 헤더 버튼·pill의 진입점 — 진행 중이면 그 Job을 그대로 보여 준다(새 업로드로 리셋하지 않음). */
  openModal: () => void;
  /** 닫기 — 진행 중이면 Job·폴링을 그대로 두고 창만 닫는다(분리). 완료/실패본은 정리한다. */
  closeModal: () => void;
  /** "다시 시도" — 표시 중인 완료/실패본을 버리고 파일 선택부터 다시. */
  reset: () => void;
  /** 업로드 시작. sheetNames가 여러 개면 서버가 순차로 변환한다(시트마다 초안 1개). */
  startUpload: (file: File, sheetNames?: string[]) => Promise<void>;
  /**
   * 진행 중 Job 취소 요청 — 즉시 끊기지 않는다(현재 시트가 끝난 뒤 반영). 만들어진 초안은
   * 남으므로 확인 모달 없이 바로 보낸다(되돌릴 수 없는 파괴가 아니다).
   */
  cancelJob: () => void;
  /** 결과 소비(에디터에서 열기) — 서버 Job을 지우고 idle로 돌아간다. */
  consumeResult: () => void;
}

const IDLE_STATE = {
  phase: 'idle' as FormImportPhase,
  progress: null,
  result: null,
  error: null,
  rawResponse: null,
};

export function useFormImportJob({ enabled, showToast }: UseFormImportJobOptions): UseFormImportJob {
  const [job, setJob] = useState<Omit<FormImportJobState, 'elapsedSeconds'>>(IDLE_STATE);
  const [modalOpen, setModalOpen] = useState(false);

  /** 콜백에서 최신 값을 읽기 위한 거울 — 폴링 콜백은 오래 살아 클로저가 낡는다. */
  const jobRef = useRef(job);
  jobRef.current = job;
  const modalOpenRef = useRef(modalOpen);
  modalOpenRef.current = modalOpen;
  /** 아직 서버에 남아 있는(우리가 지우지 않은) Job — 완료/실패 시 폴링 훅이 지우면 비운다. */
  const jobIdRef = useRef<string | null>(null);

  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  const polling = useJobPolling<FormImportProgress | null>({
    fetchProgress: (jobId) => adminApi.getFormImportProgress(jobId),
    deleteJob: (jobId) => adminApi.deleteFormImportJob(jobId),
    // cancelled를 여기 넣지 않으면 취소한 Job이 어느 술어에도 안 걸려 **폴링이 영원히 돈다**.
    // 취소는 실패가 아니고(이미 만든 초안이 남는다) 결과 화면도 완료와 같은 자리를 쓰므로
    // isFailed가 아니라 isComplete 쪽으로 보낸다 — 초안이 0건인지는 onComplete가 가른다.
    isComplete: (d) => d?.status === 'completed' || d?.status === 'cancelled',
    isFailed: (d) => d?.status === 'failed',
    onProgress: (d) => setJob((prev) => ({ ...prev, progress: d })),
    onComplete: (d) => {
      // 폴링 훅이 완료 시 Job을 삭제하므로 정리 대상에서 뺀다(중복 DELETE 방지).
      jobIdRef.current = null;
      // 성공 판정은 토스트 문구·색과 **같은 술어**를 쓴다(formImportProducedDraft) — 축이
      // 갈리면 초록 결과 화면에 빨간 "분석에 실패했습니다" 토스트가 함께 뜬다.
      if (formImportProducedDraft(d)) {
        setJob({ phase: 'result', progress: d, result: d.result, error: null, rawResponse: null });
        // 분리 상태의 완료는 **모달을 열지 않는다** — 사용자는 그 사이 다른 템플릿을 편집하고
        // 있을 수 있고(미저장 변경이 있으면 더 나쁘다), 창이 튀어나오면 작업을 가로챈다.
        // 결과가 미아가 되지 않게 하는 책임은 헤더 pill(완료 상태)과 이 토스트가 진다.
        //
        // 토스트는 **배치 전체가 끝났을 때 1회**다 — 시트마다 쏘면 47시트에 47번이 뜬다.
        if (!modalOpenRef.current) {
          showToastRef.current(describeFormImportOutcome(d), formImportOutcomeToastType(d));
        }
        return;
      }
      // 초안이 하나도 없는 취소 — 실패가 아니므로 조용히 idle로 되돌린다(다시 고를 수 있게).
      if (d?.status === 'cancelled') {
        setJob(IDLE_STATE);
        showToastRef.current(describeFormImportOutcome(d), 'info');
        return;
      }
      // 완료인데 결과가 없으면 표시할 게 없다 — 실패로 취급해 다시 시도를 유도.
      const message = '분석은 끝났지만 생성된 템플릿 정보를 받지 못했습니다. 다시 시도해주세요.';
      setJob({ phase: 'error', progress: d, result: null, error: message, rawResponse: null });
      if (!modalOpenRef.current) showToastRef.current(message, 'error');
    },
    onFailed: (d) => {
      jobIdRef.current = null;
      const message = d?.error ?? '양식 분석에 실패했습니다.';
      setJob({
        phase: 'error',
        progress: d,
        result: null,
        error: message,
        rawResponse: d?.rawResponse ?? null,
      });
      // 분리(창 닫힘) 상태의 실패는 화면에 아무 표시가 없다 — 토스트로 알린다. pill에는 실패를
      // 싣지 않는다(토스트와 중복). 상세·AI 원문은 모달을 다시 열면 그대로 남아 있다.
      if (!modalOpenRef.current) showToastRef.current(message, 'error');
    },
  });

  const { start: startPolling, stop: stopPolling } = polling;

  /** 서버에 남은 Job을 지우고 idle로 — 완료/실패본 정리 경로(진행 중 Job에는 쓰지 않는다). */
  const discardJob = useCallback(() => {
    stopPolling();
    const jobId = jobIdRef.current;
    if (jobId) {
      jobIdRef.current = null;
      void adminApi.deleteFormImportJob(jobId).catch(() => {});
    }
    setJob(IDLE_STATE);
  }, [stopPolling]);

  const openModal = useCallback(() => setModalOpen(true), []);

  const closeModal = useCallback(() => {
    // 진행 중이면 Job도 폴링도 건드리지 않는다 — 서버 deleteJob은 취소가 아니라 "결과 소비 후
    // 삭제"라, 진행 중 호출은 실행 완료를 기다렸다가 결과만 잃는다.
    if (jobRef.current.phase === 'importing') {
      setModalOpen(false);
      return;
    }
    discardJob();
    setModalOpen(false);
  }, [discardJob]);

  const reset = useCallback(() => {
    discardJob();
  }, [discardJob]);

  const consumeResult = useCallback(() => {
    discardJob();
    setModalOpen(false);
  }, [discardJob]);

  /**
   * 취소 요청 — 낙관적으로 진행률에 cancelRequested를 심어 버튼이 즉시 반응하게 한다.
   * 서버 반영(다음 폴링 tick)까지 최대 1초 비어 있으면 "안 눌렸나" 싶어 연타하게 된다.
   */
  const cancelJob = useCallback(() => {
    const jobId = jobIdRef.current;
    if (!jobId || jobRef.current.phase !== 'importing') return;
    setJob((prev) =>
      prev.progress ? { ...prev, progress: { ...prev.progress, cancelRequested: true } } : prev,
    );
    void adminApi.cancelFormImportJob(jobId).catch(() => {
      // 실패해도 폴링이 실제 상태를 다음 tick에 덮어쓴다 — 여기서 되돌리면 깜빡인다.
      showToastRef.current('취소 요청을 보내지 못했습니다. 다시 시도해주세요.', 'error');
    });
  }, []);

  const startUpload = useCallback(
    async (file: File, sheetNames?: string[]) => {
      setJob({ ...IDLE_STATE, phase: 'importing' });
      try {
        // 시트 미선택("자동")이면 빈 배열 — 서버가 내용이 있는 첫 시트를 고른다
        const res = await adminApi.startFormImport(file, sheetNames);
        if (res.success && res.data?.jobId) {
          jobIdRef.current = res.data.jobId;
          startPolling(res.data.jobId);
          return;
        }
        setJob({
          ...IDLE_STATE,
          phase: 'error',
          error: res.error?.message ?? '업로드에 실패했습니다.',
        });
      } catch (err) {
        // 서버 검증 실패(형식·용량·PDF 페이지 수)는 여기로 온다 — 메시지를 그대로 보여 준다.
        const message =
          (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
            ?.message ?? (err as Error).message;
        setJob({
          ...IDLE_STATE,
          phase: 'error',
          error: message || '업로드 중 오류가 발생했습니다.',
        });
      }
    },
    [startPolling],
  );

  // 새로고침 복구 — 진행 중이면 폴링을 이어받고, 완료본은 결과를 되살린다(초안은 이미 생성돼
  // 있어 되살리지 않으면 목록에서만 발견되는 미아가 된다).
  //
  // 복구는 **어느 경우에도 모달을 자동으로 열지 않는다.** 진행 중/완료 두 갈래를 pill 하나로
  // 통일한 이유: 새로고침 직후라면 가로챌 작업이 없어 열어도 무해하지만, "복구는 열리고 분리
  // 상태의 완료는 안 열린다"처럼 동작이 갈리면 같은 완료 이벤트가 상황에 따라 다르게 굴어
  // 오히려 헷갈린다. 미아 방지 책임은 pill(진행 중/완료 상태)과 토스트가 함께 진다.
  // 실패본은 토스트로 알리고 정리한다(페이지 진입을 막지 않기 위함).
  useEffect(() => {
    if (!enabled) return;
    void runJobRecovery([
      {
        name: 'form-import',
        fetchActive: () => adminApi.getActiveFormImportJob(),
        isProcessing: (d: ActiveFormImportJob) => d.data?.status === 'processing',
        onProcessing: (d: ActiveFormImportJob) => {
          jobIdRef.current = d.jobId;
          // 서버가 준 진행률을 그대로 심는다 — 첫 tick(1초)까지 빈 화면·0초로 보이지 않는다.
          setJob({ ...IDLE_STATE, phase: 'importing', progress: d.data });
          startPolling(d.jobId);
          // 모달은 열지 않는다 — pill이 "양식 변환 중"으로 계속 보여 주고, 클릭하면 열린다.
        },
        isFailed: (d: ActiveFormImportJob) => d.data?.status === 'failed',
        onFailed: async (d: ActiveFormImportJob) => {
          showToastRef.current(d.data?.error ?? '이전 양식 분석이 실패했습니다.', 'error');
          await adminApi.deleteFormImportJob(d.jobId).catch(() => {});
        },
        // 취소로 끝난 Job도 복구 대상이다 — 그때까지 만들어진 초안이 result에 있고, 되살리지
        // 않으면 목록에서만 발견되는 미아가 된다(완료본과 같은 이유).
        isCompleted: (d: ActiveFormImportJob) =>
          (d.data?.status === 'completed' || d.data?.status === 'cancelled') &&
          formImportProducedDraft(d.data),
        onCompleted: (d: ActiveFormImportJob) => {
          if (!formImportProducedDraft(d.data)) return;
          jobIdRef.current = d.jobId;
          setJob({ ...IDLE_STATE, phase: 'result', progress: d.data, result: d.data.result });
          // 자동 열기 대신 pill 완료 상태 + 토스트(위 주석의 통일 근거).
          showToastRef.current(
            describeFormImportOutcome(d.data),
            formImportOutcomeToastType(d.data),
          );
        },
      },
    ]);
  }, [enabled, startPolling]);

  const elapsedSeconds = useElapsedSeconds(job.phase === 'importing', job.progress?.elapsedMs);

  return {
    state: { ...job, elapsedSeconds },
    modalOpen,
    openModal,
    closeModal,
    reset,
    startUpload,
    cancelJob,
    consumeResult,
  };
}
